import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeftToLine, ArrowRightToLine } from "lucide-react";
import clsx from "clsx";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { ContextFileViewer } from "@/components/feature/layout/left-sidebar/contextFileViewer";
import { Toggle } from "@/components/feature/layout/toggle";
import { FileItem, Item } from "@/types/fse";
import { FlattenedItem } from "@/modules/chat/chat-ui.types";
import { useGetFileDetailsQuery } from "@/api/files";
import { flatFileSystem, getFilesInFolder, getParentLocations } from "@/components/feature/layout/left-sidebar/helpers";
import { useAskFile } from "@/modules/chat/hooks/useAskFile";

interface Props {
  systemId: string;
  onFileSelect: (file: FileItem, isSelected: boolean) => void;
  isVisible: boolean;
  onToggleVisibility: () => void;
  showFolders: boolean;
  showToggleAll?: boolean;
  singleFilesSelection?: boolean;
}

export const ContextSidebar: React.FC<Props> = React.memo(function ContextSidebar({
  systemId,
  onFileSelect,
  isVisible,
  onToggleVisibility,
  showToggleAll,
  showFolders,
  singleFilesSelection,
}) {
  const { setCurrentPdfId, setSelectedFiles: contextSetSelectedFiles } = useCurrentPdf();
  const handleAskFile = useAskFile();
  const [flattenedItems, setFlattenedItems] = React.useState<FlattenedItem[]>([]);
  const [expandedFolders, setExpandedFolders] = React.useState<Set<string>>(new Set());
  const [selectedFiles, setSelectedFiles] = React.useState<Set<string>>(new Set());

  const {
    data: rootItem,
    error,
    isLoading,
    refetch: loadFileSystem,
  } = useGetFileDetailsQuery({ fse_id: systemId, depth: 50 });

  React.useEffect(() => {
    if (rootItem && expandedFolders.size === 0) {
      const allFolderIds = new Set<string>();
      const collectIds = (item: Item) => {
        if (item.type === "folder") {
          allFolderIds.add(item.id);
          Object.values(item.items).forEach(collectIds);
        }
      };
      collectIds(rootItem);
      setExpandedFolders(allFolderIds);
    }
  }, [rootItem, expandedFolders.size]);

  React.useEffect(() => {
    contextSetSelectedFiles(Array.from(selectedFiles));
  }, [selectedFiles, contextSetSelectedFiles]);

  React.useEffect(() => {
    if (rootItem) {
      const rows = flatFileSystem(rootItem, 0, expandedFolders, showFolders);
      setFlattenedItems(rows);
      const groups = rows.map(item => (item.type === "file" ? item.document_group_id : undefined)).filter(Boolean);

      const rowsFiles = rows
        .filter(item => item.type === "file" && !item.documents_groups?.some((row: string) => groups.includes(row)))
        .map(item => item.id);

      if (singleFilesSelection) {
        if (rowsFiles[0]) {
          setSelectedFiles(new Set([rowsFiles[0]]));
        }
      } else {
        setSelectedFiles(new Set(rowsFiles));
      }
    }
  }, [singleFilesSelection, rootItem, expandedFolders, showFolders]);

  const toggleFolder = React.useCallback((folderId: string) => {
    setExpandedFolders(prev => {
      const newExpandedFolders = new Set(prev);
      if (newExpandedFolders.has(folderId)) {
        newExpandedFolders.delete(folderId);
      } else {
        newExpandedFolders.add(folderId);
      }
      return newExpandedFolders;
    });
  }, []);

  const handleFileSelect = React.useCallback(
    (file: FileItem, isSelected: boolean) => {
      onFileSelect(file, isSelected);
      setSelectedFiles(prev => {
        const newSet = singleFilesSelection ? new Set<string>() : new Set(prev);

        if (isSelected || singleFilesSelection) {
          newSet.add(file.id);
        } else {
          newSet.delete(file.id);
        }

        return newSet;
      });
    },
    [onFileSelect, singleFilesSelection],
  );

  const handleFileClick = React.useCallback(
    (file: FileItem) => {
      setCurrentPdfId(file.id);
    },
    [setCurrentPdfId],
  );

  const isAllSelected = flattenedItems.filter(item => item.type === "file").every(item => selectedFiles.has(item.id));

  const handleToggleSelect = (enabled: boolean) => {
    flattenedItems.forEach(item => {
      if (item.type === "file") {
        handleFileSelect(item, enabled);
      }
    });
  };

  // Helper function to determine folder selection state
  const getFolderSelectionState = React.useCallback(
    (folderId: string): "all" | "some" | "none" => {
      if (!rootItem) return "none";

      const filesInFolder = getFilesInFolder(folderId, rootItem, { skipRejected: false });
      const selectedFilesInFolder = filesInFolder.filter(fileId => selectedFiles.has(fileId));

      if (selectedFilesInFolder.length === 0) return "none";
      if (selectedFilesInFolder.length === filesInFolder.length) return "all";
      return "some";
    },
    [rootItem, selectedFiles],
  );

  // Handler for folder-level selection toggle
  const handleFolderToggle = React.useCallback(
    (folderId: string) => {
      if (!rootItem) return;

      const filesInFolder = getFilesInFolder(folderId, rootItem, { skipRejected: false });
      const currentState = getFolderSelectionState(folderId);
      const shouldSelect = currentState !== "all";

      // Create a flattened item for each file to use with handleFileSelect
      const relevantFlattenedItems = flattenedItems.filter(
        item => item.type === "file" && filesInFolder.includes(item.id),
      );

      relevantFlattenedItems.forEach(item => {
        handleFileSelect(item as FileItem, shouldSelect);
      });
    },
    [rootItem, getFolderSelectionState, flattenedItems, handleFileSelect],
  );

  return (
    <motion.div
      className={clsx("h-full flex-shrink-0 bg-ui-sidebar-background", {
        "min-w-[280px] max-w-[360px] overflow-y-auto 2xl:max-w-[380px]": isVisible,
        "w-10": !isVisible,
      })}
      initial={{ width: isVisible ? "0px" : "40px" }}
      animate={{ width: isVisible ? "min(20%, 360px)" : "40px" }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between bg-ui-sidebar-background p-2">
          <button onClick={onToggleVisibility} className="rounded p-2 hover:bg-desert-sand-300">
            {isVisible ? <ArrowLeftToLine size={20} /> : <ArrowRightToLine size={20} />}
          </button>
        </div>

        <AnimatePresence>
          {isVisible && (
            <motion.div
              className="flex-grow overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="flex p-4 text-lg font-bold">
                Select files
                {showToggleAll && (
                  <div className="ml-auto mt-2 flex-shrink-0">
                    <Toggle enabled={isAllSelected} size="xs" onChange={handleToggleSelect} />
                  </div>
                )}
              </h2>
              <div className="h-[calc(100vh-6rem)] overflow-y-auto px-5">
                <div className="pb-24">
                  <ContextFileViewer
                    loading={isLoading}
                    error={error}
                    rootItem={rootItem}
                    flattenedItems={flattenedItems}
                    loadFileSystem={loadFileSystem}
                    showFolders={showFolders}
                    toggleFolder={toggleFolder}
                    handleFileClick={handleFileClick}
                    handleFileSelect={handleFileSelect}
                    handleAskFile={handleAskFile}
                    selectedFiles={selectedFiles}
                    getParentLocations={getParentLocations}
                    handleFolderToggle={handleFolderToggle}
                    getFolderSelectionState={getFolderSelectionState}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
});
